import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getContacts } from "../../api/apiServices";

export default function ContactList() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ name: "", email: "", budget: "" });
  const [selectedContact, setSelectedContact] = useState(null);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams(
        Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v.trim() !== "")
        )
      ).toString();

      const data = await getContacts(query);
      setContacts(data.contacts || []);
    } catch (err) {
      console.error("Error fetching contacts:", err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchContacts();
  };

  return (
    <div className="p-6 sm:p-10 relative">
      <h2 className="text-3xl font-bold text-slate-800 mb-8">
        📩 Contact Form Submissions
      </h2>

      <form
        onSubmit={handleSearch}
        className="grid sm:grid-cols-4 gap-4 mb-8 bg-white p-4 rounded-xl shadow-md border"
      >
        <input
          type="text"
          name="name"
          placeholder="Filter by name"
          value={filters.name}
          onChange={handleFilterChange}
          className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
        />
        <input
          type="email"
          name="email"
          placeholder="Filter by email"
          value={filters.email}
          onChange={handleFilterChange}
          className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
        />
        <select
          name="budget"
          value={filters.budget}
          onChange={handleFilterChange}
          className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
        >
          <option value="">All Budgets</option>
          <option value="5k-10k">$5k - $10k</option>
          <option value="10k-25k">$10k - $25k</option>
          <option value="25k-50k">$25k - $50k</option>
          <option value="50k+">$50k+</option>
        </select>
        <button
          type="submit"
          className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold px-6 py-2 rounded-lg shadow hover:scale-[1.02] transition-all"
        >
          Search
        </button>
      </form>

      {loading ? (
        <div className="flex justify-center items-center py-20">
          <i className="ri-loader-4-line animate-spin text-3xl text-emerald-600"></i>
        </div>
      ) : contacts.length === 0 ? (
        <p className="text-slate-500 text-center py-10 font-medium">
          No contact messages found.
        </p>
      ) : (
        <div className="overflow-x-auto bg-white rounded-xl shadow-md border">
          <table className="min-w-full border-collapse">
            <thead className="bg-slate-100 text-slate-700 uppercase text-sm font-semibold">
              <tr>
                <th className="py-3 px-4 text-left">Name</th>
                <th className="py-3 px-4 text-left">Email</th>
                <th className="py-3 px-4 text-left">Company</th>
                <th className="py-3 px-4 text-left">Budget</th>
                <th className="py-3 px-4 text-left">Message</th>
                <th className="py-3 px-4 text-left">Date</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c) => (
                <tr
                  key={c._id}
                  onClick={() => setSelectedContact(c)}
                  className="border-b border-slate-200 hover:bg-slate-50 transition cursor-pointer"
                >
                  <td className="py-3 px-4 font-medium text-slate-800">
                    {c.name}
                  </td>
                  <td className="py-3 px-4 text-slate-600">{c.email}</td>
                  <td className="py-3 px-4 text-slate-600">
                    {c.company || "-"}
                  </td>
                  <td className="py-3 px-4 text-slate-600">{c.budget || "-"}</td>
                  <td className="py-3 px-4 text-slate-600 max-w-xs truncate">
                    {c.message || "-"}
                  </td>
                  <td className="py-3 px-4 text-slate-500 text-sm">
                    {new Date(c.createdAt).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Contact Detail Drawer */}
      <AnimatePresence>
        {selectedContact && (
          <>
            {/* Backdrop */}
            <motion.div
              className="fixed inset-0 bg-black/40 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedContact(null)}
            ></motion.div>

            {/* Drawer */}
            <motion.div
              className="fixed top-0 right-0 w-full sm:w-[420px] h-full bg-white shadow-2xl z-50 flex flex-col"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 80, damping: 20 }}
            >
              <div className="flex justify-between items-center p-6 border-b border-slate-200">
                <h3 className="text-xl font-semibold text-slate-800">
                  Contact Details
                </h3>
                <button
                  onClick={() => setSelectedContact(null)}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="p-6 space-y-4 overflow-y-auto">
                <div>
                  <p className="text-sm text-slate-500">Name</p>
                  <p className="font-medium text-slate-800">
                    {selectedContact.name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium text-slate-800">
                    {selectedContact.email}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Company</p>
                  <p className="font-medium text-slate-800">
                    {selectedContact.company || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Budget</p>
                  <p className="font-medium text-slate-800">
                    {selectedContact.budget || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Project Type</p>
                  <p className="font-medium text-slate-800">
                    {selectedContact.projectType || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Timeline</p>
                  <p className="font-medium text-slate-800">
                    {selectedContact.timeline || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Message</p>
                  <p className="font-medium text-slate-800 whitespace-pre-wrap">
                    {selectedContact.message || "-"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Submitted At</p>
                  <p className="text-slate-600 text-sm">
                    {new Date(selectedContact.createdAt).toLocaleString()}
                  </p>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
